/* eslint-disable no-param-reassign */
import {
  filterViewFields,
  transformForSubmit as formsSystemTransformForSubmit,
} from 'platform/forms-system/src/js/helpers';
import recordEvent from 'platform/monitoring/record-event';
import {
  rehydrateFieldSelections,
  withCombinedStreet,
} from 'platform/shared/health-apps';
import {
  getAgeInYears,
  isNewSubmission,
  isObject,
  isSupportingDocument,
  toHash,
} from '../utils/helpers';

const NON_APPLICANT_ROLE_PREFIX = Object.freeze({
  sponsor: 'sponsor',
  other: 'certifier',
});

const getCurrentDate = () => new Date().toISOString().split('T')[0];

const getRolePrefix = role =>
  role === 'applicant' ? null : NON_APPLICANT_ROLE_PREFIX[role] || 'certifier';

const getRoleValue = (data = {}, role, suffix) => {
  const prefix = getRolePrefix(role);
  return prefix ? data?.[`${prefix}${suffix}`] : undefined;
};

const getTrueKeys = (obj = {}) =>
  Object.keys(obj).filter(key => obj[key] === true);

const getRelationship = relationship => getTrueKeys(relationship).join('; ');

const pickDocumentFields = ({ name, confirmationCode, attachmentId }) => ({
  name,
  confirmationCode,
  attachmentId,
});

const mapHealthInsuranceToApplicants = (
  data,
  advantageParticipants = new Set(),
) => {
  // Create a deep copy to avoid mutations
  const result = JSON.parse(JSON.stringify(data));

  // Map Medicare plans to applicants
  result.medicare.forEach(plan => {
    result.applicants
      .filter(
        applicant =>
          plan.medicareParticipant === toHash(applicant.applicantSsn),
      )
      .forEach(applicant => {
        // Initialize Medicare array if it doesn't exist
        applicant.medicare = applicant.medicare || [];
        const planType = String(plan?.medicarePlanType ?? '')
          .trim()
          .toLowerCase();
        const isAdvantage =
          planType === 'c' ||
          advantageParticipants.has(plan?.medicareParticipant) ||
          Boolean(plan?.medicarePartCCarrier);
        applicant.applicantMedicareAdvantage =
          Boolean(applicant.applicantMedicareAdvantage) || isAdvantage;
        // original 10-10d form produces this medicareStatus field, so we need
        // it to fill the PDF on the backend
        applicant.applicantMedicareStatus = { eligibility: 'enrolled' };
        applicant.medicare.push(plan);
      });
  });

  // Map health insurance policies to applicants
  result.healthInsurance.forEach(policy => {
    // Get hashes of applicants participating in this policy
    const participantHashes = getTrueKeys(policy.healthcareParticipants);

    result.applicants
      .filter(applicant =>
        participantHashes.includes(toHash(applicant.applicantSsn)),
      )
      .forEach(applicant => {
        // Initialize health insurance array
        applicant.healthInsurance = applicant.healthInsurance || [];

        // Format middle name as initial
        if (applicant.applicantName?.middle) {
          applicant.applicantName.middle =
            applicant.applicantName.middle.charAt(0) || '';
        }

        // Add policy and set flag
        applicant.healthInsurance.push(policy);
        applicant.hasOtherHealthInsurance = true;
        // original 10-10d form produces this applicantHasOhi prop, so we need
        // it to fill the PDF on the backend
        applicant.applicantHasOhi = { hasOhi: 'yes' };
      });
  });

  result.certificationDate = getCurrentDate();

  return {
    ...result,
    veteran: data.veteran,
  };
};

const formatDatesForPDF = data => {
  const dateFieldPattern = /date|dob|dod/i;
  const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

  const walk = value => {
    if (Array.isArray(value)) return value.map(walk);

    if (!value || typeof value !== 'object') return value;

    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => {
        const isDateField =
          dateFieldPattern.test(key) &&
          typeof nestedValue === 'string' &&
          isoDatePattern.test(nestedValue);

        return [
          key,
          isDateField
            ? nestedValue.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$2-$3-$1')
            : walk(nestedValue),
        ];
      }),
    );
  };

  return walk(data);
};

const collectSupportingDocuments = data => {
  const getDocumentKey = doc =>
    [doc?.confirmationCode, doc?.attachmentId || doc?.name || ''].join('::');

  const dedupeDocuments = docs => {
    const seen = new Set();
    return docs.filter(doc => {
      const key = getDocumentKey(doc);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const collect = value => {
    if (isSupportingDocument(value)) return [pickDocumentFields(value)];

    if (value?.attachmentFile && isSupportingDocument(value.attachmentFile)) {
      return [
        {
          ...pickDocumentFields(value.attachmentFile),
          attachmentId: value.attachmentId,
        },
      ];
    }

    if (Array.isArray(value)) {
      if (value.length > 0 && isSupportingDocument(value[0])) {
        return value.map(pickDocumentFields);
      }
      return value.flatMap(collect);
    }

    if (isObject(value)) return Object.values(value).flatMap(collect);

    return [];
  };

  const { applicants, ...rest } = data;

  const applicantDocs = (applicants ?? []).flatMap(applicant =>
    collect(applicant).map(doc => ({
      ...doc,
      applicantName: applicant.applicantName,
    })),
  );

  const dedupedApplicantDocs = dedupeDocuments(applicantDocs);

  const applicantDocKeys = new Set(
    dedupedApplicantDocs.map(doc => getDocumentKey(doc)),
  );

  const nonApplicantDocs = collect(rest).filter(
    doc => !applicantDocKeys.has(getDocumentKey(doc)),
  );

  return dedupeDocuments([...nonApplicantDocs, ...dedupedApplicantDocs]);
};

const buildCertificationData = (data = {}) => {
  const { certifierRole } = data;
  const date = getCurrentDate();

  if (certifierRole === 'applicant') return { date };

  const name = getRoleValue(data, certifierRole, 'Name') || {};
  const address = getRoleValue(data, certifierRole, 'Address') || {};

  return {
    date,
    firstName: name.first || '',
    middleInitial: name.middle || '',
    lastName: name.last || '',
    phoneNumber: getRoleValue(data, certifierRole, 'Phone') || '',
    streetAddress: address.streetCombined || '',
    city: address.city || '',
    state: address.state || '',
    postalCode: address.postalCode || '',
    country: address.country || '',
    relationship: getRelationship(
      getRoleValue(data, certifierRole, 'Relationship'),
    ),
  };
};

const buildPrimaryContactInfo = (data = {}) => {
  const { certifierRole } = data;

  if (certifierRole === 'applicant') {
    const appIndex = Number(data['view:certifierApplicantIndex'] ?? 0);
    const applicant = data?.applicants?.[appIndex] || {};
    const fallbackEmail = data?.applicants?.[0]?.applicantEmailAddress;

    return {
      name: applicant.applicantName,
      email: applicant.applicantEmailAddress || fallbackEmail,
      phone: applicant.applicantPhone,
    };
  }

  return {
    name: getRoleValue(data, certifierRole, 'Name'),
    email: getRoleValue(data, certifierRole, 'Email'),
    phone: getRoleValue(data, certifierRole, 'Phone'),
  };
};

const transformAddress = address => {
  if (!address || typeof address !== 'object') return address;
  return withCombinedStreet(filterViewFields(address));
};

const buildExistingSubmission = (data, formConfig) => {
  const {
    certifierRole,
    submissionType,
    contactName,
    contactEmail,
    applicantName,
    applicantDob,
    applicantMemberNumber,
    sponsorName,
    sponsorDob,
    sponsorSsn,
    statementOfTruthSignature,
  } = data;

  let primaryContactName;
  if (certifierRole === 'applicant') {
    primaryContactName = applicantName;
  } else if (certifierRole === 'sponsor') {
    primaryContactName = sponsorName;
  } else {
    primaryContactName = contactName;
  }

  const submissionData = {
    certifierRole,
    submissionType,
    primaryContactInfo: {
      name: primaryContactName,
      email: contactEmail,
    },
    veteran: {
      fullName: sponsorName,
      ssnOrTin: sponsorSsn,
      dateOfBirth: sponsorDob,
    },
    applicants: [
      {
        applicantName,
        applicantDob,
        applicantMemberNumber,
      },
    ],
    certification: { date: getCurrentDate() },
    supportingDocs: collectSupportingDocuments(data),
    statementOfTruthSignature,
    formNumber: formConfig.formId,
  };

  return JSON.stringify(formatDatesForPDF(submissionData));
};

const getSubmissionTypeEventName = data =>
  isNewSubmission(data)
    ? '10-10d_new_application'
    : '10-10d_update_existing_application';

/**
 * Main transformer function that prepares form data for submission
 * @param {Object} formConfig - Form configuration object containing form settings and metadata
 * @param {Object} form - Form object containing the user-submitted data
 * @param {Boolean} [disableAnalytics=false] - Flag to disable analytics event tracking
 * @returns {string} JSON string of transformed data ready for submission
 */
export default function transformForSubmit(
  formConfig,
  form,
  disableAnalytics = false,
) {
  const rawData = form?.data || {};
  const filteredData = JSON.parse(
    formsSystemTransformForSubmit(formConfig, form),
  );
  const hydratedData = {
    ...rehydrateFieldSelections(
      formConfig,
      rawData,
      filteredData,
      transformAddress,
    ),
    sponsorAddress: transformAddress(filteredData.sponsorAddress), // sponsor address has no field selection
  };

  // Track submission type analytics
  if (!disableAnalytics) {
    recordEvent({ event: getSubmissionTypeEventName(rawData) });
  }

  if (!isNewSubmission(rawData)) {
    return buildExistingSubmission(hydratedData, formConfig);
  }

  const marriageDate = hydratedData.applicants?.find(
    applicant =>
      applicant?.applicantRelationshipToSponsor === 'spouse' &&
      applicant?.dateOfMarriageToSponsor,
  )?.dateOfMarriageToSponsor;

  const veteranData = {
    fullName: hydratedData.sponsorName || {},
    ssnOrTin: hydratedData.sponsorSsn || '',
    dateOfBirth: hydratedData.sponsorDob || '',
    phoneNumber: hydratedData.sponsorPhone || '',
    email: hydratedData.sponsorEmail || '',
    address: hydratedData.sponsorAddress || {},
    sponsorIsDeceased: hydratedData.sponsorIsDeceased ?? false,
    dateOfDeath: hydratedData.sponsorDod || '',
    dateOfMarriage: marriageDate || '',
    isActiveServiceDeath: hydratedData.sponsorDeathConditions,
  };

  const initialData = {
    veteran: veteranData,
    certification: buildCertificationData(hydratedData),
    applicants: hydratedData.applicants || [],
    healthInsurance: hydratedData.healthInsurance || [],
    medicare: hydratedData.medicare || [],
    supportingDocs: [],
  };

  const advantageParticipants = new Set(
    (form?.data?.medicare || [])
      .filter(
        p =>
          String(p?.medicarePlanType ?? '')
            .trim()
            .toLowerCase() === 'c',
      )
      .map(p => p?.medicareParticipant)
      .filter(Boolean),
  );
  const transformedData = mapHealthInsuranceToApplicants(
    initialData,
    advantageParticipants,
  );
  transformedData.supportingDocs = collectSupportingDocuments(transformedData);

  transformedData.hasApplicantOver65 = transformedData.applicants.some(a => {
    const age = getAgeInYears(a.applicantDob);
    return Number.isFinite(age) && age >= 65;
  });

  transformedData.certifierRole = hydratedData.certifierRole;
  transformedData.statementOfTruthSignature =
    hydratedData.statementOfTruthSignature;

  transformedData.primaryContactInfo = buildPrimaryContactInfo(hydratedData);

  return JSON.stringify({
    ...formatDatesForPDF(transformedData),
    formNumber: formConfig.formId,
  });
}
